/**
 * LanguageSwitcher — flag-only minimal switcher.
 *
 * Owen Jul 2026 rewrite:
 *   • Italian sits at the TOP of the picker and its flag is rendered
 *     slightly larger than the others (Owen's Italian heritage — this
 *     is the Coliseum, after all).
 *   • The four English variants (NZ / US / GB / CA) are gone.
 *     There is ONE English entry, marked with two little flags on
 *     wooden poles: the New Zealand flag (swings LEFT) and the Māori
 *     Tino Rangatiratanga flag (swings RIGHT). Reflects Owen's own
 *     kiwi + Māori identity.
 *   • Every pick still fires PATCH /api/me/language so the LLM,
 *     Whisper, emails and PDFs speak the same language as the UI.
 *
 * Previous versions supported four flag-variants that all routed to
 * `en` in localStorage — that machinery is retired. The last saved
 * variant is silently ignored on hydrate.
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronDown, Languages } from 'lucide-react';
import axios from 'axios';

import { MaoriFlag, NZFlag, ItalyFlag, FranceFlag, SpainFlag, MalaysiaFlag, IndiaFlag, RussiaFlag, TaiwanFlag, ChinaFlag } from './FlagsPair';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Fire-and-forget: persist the user's language choice to the backend so
// every server-generated surface (LLM system prompts, Whisper transcription,
// emails, PDFs) uses their language. Silent-fail for signed-out visitors —
// their choice still lives in localStorage until they authenticate.
// Owen Jul 2026 — Italian-first push, Checkpoint 1.
async function persistLanguageToBackend(langCode) {
  try {
    await axios.patch(
      `${API}/me/language`,
      { lang: langCode },
      { withCredentials: true, timeout: 4000 }
    );
  } catch {
    // Signed-out or offline — the frontend will re-send on next sign-in.
  }
}

// Order matters — Italian is deliberately first (heritage of the platform).
// `flagRender` renders a custom SVG for languages that need one.
// `emphasis` is a small size bump for the flag glyph — Italian gets the
// slightly-larger flag Owen asked for.
// Owen Jul 2026 — Māori (`mi`) is now a first-class picker option with its
// own Tino Rangatiratanga flag. The old English NZ+Māori PAIRED icon is
// retired: English now shows the NZ flag alone, and Māori has the
// Tino Rangatiratanga flag alone. Two flags, two rows, cleaner picker.
// Owen Feb 2026 — every flag now uses inline SVG. Emoji regional-indicator
// glyphs (🇮🇹🇫🇷🇪🇸) don't render on Windows Chrome/Edge (Microsoft's
// no-national-flags policy) so laptop users were seeing blank/text-code
// slots for Italian, French and Spanish. Inline SVGs are crisp at any
// size, work on every OS, and stay consistent with the existing NZ / Māori
// flag treatment. Italian keeps its emphasis flag-bump.
const LANGS = [
  { code: 'it',    flag: null, native: 'Italiano',       emphasis: true,  flagRender: (size) => <ItalyFlag    h={size === 'lg' ? 20 : 14} w={size === 'lg' ? 32 : 22} /> },
  { code: 'en',    flag: null, native: 'English',        emphasis: false, flagRender: (size) => <NZFlag       h={size === 'lg' ? 18 : 14} w={size === 'lg' ? 28 : 22} /> },
  { code: 'mi',    flag: null, native: 'Te Reo Māori',   emphasis: false, flagRender: (size) => <MaoriFlag    h={size === 'lg' ? 18 : 14} w={size === 'lg' ? 28 : 22} /> },
  { code: 'fr',    flag: null, native: 'Français',       emphasis: false, flagRender: (size) => <FranceFlag   h={size === 'lg' ? 18 : 14} w={size === 'lg' ? 28 : 22} /> },
  { code: 'es',    flag: null, native: 'Español',        emphasis: false, flagRender: (size) => <SpainFlag    h={size === 'lg' ? 18 : 14} w={size === 'lg' ? 28 : 22} /> },
  { code: 'ms',    flag: null, native: 'Bahasa Malaysia', emphasis: false, flagRender: (size) => <MalaysiaFlag h={size === 'lg' ? 18 : 14} w={size === 'lg' ? 28 : 22} /> },
  // Owen Jul 2026 — Hindi comes online. Full 1,214-key translation
  // already in hi.json, welcome email localised via Claude Sonnet
  // voice-brief. Enables sharing to family in India.
  { code: 'hi',    flag: null, native: 'हिन्दी',            emphasis: false, flagRender: (size) => <IndiaFlag    h={size === 'lg' ? 18 : 14} w={size === 'lg' ? 28 : 22} /> },
  // Owen Feb 2026 — Russian (`ru`) added as a first-class picker option.
  // Full 1,216-key ru.json bootstrapped via Claude Sonnet with polite 'Вы'
  // register. Enables sharing to family members and readers across Russia
  // and the Russian-speaking diaspora.
  { code: 'ru',    flag: null, native: 'Русский',        emphasis: false, flagRender: (size) => <RussiaFlag   h={size === 'lg' ? 18 : 14} w={size === 'lg' ? 28 : 22} /> },
  // Chinese (zh, zh-TW) translations are complete in resources but
  // temporarily hidden from the picker per Owen's request (2026-07-05).
  // To re-enable, add these two entries back:
  //   { code: 'zh',    flag: null, native: '简体中文', ... flagRender: () => <ChinaFlag  ... /> },
  //   { code: 'zh-TW', flag: null, native: '繁體中文', ... flagRender: () => <TaiwanFlag ... /> },
];

const LanguageSwitcher = ({ className = '', dropUp = false }) => {
  const { i18n } = useTranslation();
  // Preserve region tag for zh-TW / zh-HK / zh-CN etc; fall back to bare code otherwise.
  const raw = i18n.language || 'en';
  const currentLang = raw.startsWith('zh-') ? raw : raw.substring(0, 2);

  const current = LANGS.find(l => l.code === currentLang) || LANGS[0];

  const [open, setOpen] = useState(false);
  // Owen Jul 2026 — auto-detect which side of the pill the dropdown
  // should anchor to. When the pill sits on the LEFT edge of the
  // viewport (FloatingUtilities strip on mobile), 'right-0' would fly
  // the dropdown off-screen; we flip to 'left-0'. Recomputed at every
  // open so it stays right if the layout shifts (e.g. resize / rotate).
  const [anchorSide, setAnchorSide] = useState('right');
  const wrapRef = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const openMenu = () => {
    // Recompute anchor just before the dropdown mounts.
    try {
      const rect = wrapRef.current?.getBoundingClientRect();
      if (rect) {
        // If pill's left edge is in the left third of the viewport,
        // anchor the dropdown to left so it opens rightward.
        setAnchorSide(rect.left < window.innerWidth / 3 ? 'left' : 'right');
      }
    } catch { /* getBoundingClientRect not available — fall back to right */ }
    setOpen(o => !o);
  };

  const pick = (entry) => {
    i18n.changeLanguage(entry.code);
    try { localStorage.setItem('i18nextLng', entry.code); } catch { /* ignore */ }
    persistLanguageToBackend(entry.code);
    setOpen(false);
  };

  const isActive = (entry) => entry.code === currentLang;

  // Renders the flag glyph for a given entry at either 'sm' (default,
  // used inside the dropdown list) or 'lg' (used in the closed-state
  // button chip). Italian gets a small bump — `emphasis: true`.
  const renderFlag = (entry, size = 'sm') => {
    if (entry.flagRender) return entry.flagRender(size);
    const base = size === 'lg' ? 20 : 16;
    const bumped = size === 'lg' ? 26 : 20;
    return (
      <span
        aria-hidden="true"
        style={{
          fontSize: `${entry.emphasis ? bumped : base}px`,
          lineHeight: 1,
          display: 'inline-block',
          verticalAlign: 'middle',
        }}
      >
        {entry.flag}
      </span>
    );
  };

  return (
    <div ref={wrapRef} className={`relative inline-flex ${className}`} data-testid="language-switcher">
      <button
        type="button"
        onClick={openMenu}
        data-testid="lang-toggle"
        aria-haspopup="listbox"
        aria-expanded={open}
        title={current.native}
        className="h-10 px-3 rounded-full inline-flex items-center gap-2 leading-none bg-[#2A2522]/95 backdrop-blur-sm border border-[#D4AF37] hover:bg-[#1C1917] hover:border-[#D4AF37] hover:shadow-[0_4px_14px_rgba(212,175,55,0.4)] shadow-[0_3px_10px_rgba(0,0,0,0.5)] transition-all focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/60"
      >
        <Languages aria-hidden="true" className="w-[14px] h-[14px] text-[#D4AF37]" />
        {renderFlag(current, 'lg')}
        <span
          aria-hidden="true"
          className="text-[10px] font-medium tracking-wider text-[#D4AF37] uppercase"
          data-testid="lang-code-label"
        >
          {currentLang.toUpperCase()}
        </span>
        <ChevronDown
          aria-hidden="true"
          className={`w-3 h-3 text-[#D4AF37]/80 transition-transform ${open ? 'rotate-180' : ''}`}
        />
        <span className="sr-only">{current.native}</span>
      </button>
      {open && (
        <ul
          role="listbox"
          className={`absolute ${dropUp ? 'bottom-full mb-2' : 'top-full mt-2'} ${anchorSide === 'left' ? 'left-0' : 'right-0'} min-w-[13rem] bg-[#1C1917] border border-[#3D3531] rounded-md shadow-2xl py-1 z-[60]`}
          data-testid="lang-dropdown"
        >
          {LANGS.map(l => {
            const active = isActive(l);
            return (
              <li key={l.code}>
                <button
                  type="button"
                  onClick={() => pick(l)}
                  data-testid={`lang-${l.code}`}
                  role="option"
                  aria-selected={active}
                  className={`w-full flex items-center gap-3 px-3 py-2 text-left transition-colors ${
                    active
                      ? 'text-[#D4AF37] bg-[#2A2522]'
                      : 'text-[#C4B8AE] hover:bg-[#2A2522] hover:text-[#F4EFE6]'
                  }`}
                >
                  <span className="w-11 shrink-0 flex items-center justify-center">
                    {renderFlag(l, 'sm')}
                  </span>
                  <span
                    className={`flex-1 italic ${l.emphasis ? 'text-base' : 'text-sm'}`}
                    style={{fontFamily: 'Cormorant Garamond, serif'}}
                  >
                    {l.native}
                  </span>
                  {active && <Check className="w-3.5 h-3.5 text-[#D4AF37]" />}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default LanguageSwitcher;
